// Paket düzenleyici başlığı (P2): ad (büyük) + kod + sektör pill; ikinci satır taslağı yansıtan
// sayaçlar ("15 gösterge (10'u otomatik) · 1 sette kullanılıyor · Son değişiklik …"); açıklama;
// sağda ✦ AI ile öner (ikincil, mor ton) + + Gösterge ekle (birincil) + ⋯ menüsü (Kimliği düzenle ·
// Bu paketten set kur · Arşivle). Menü `role=menu`: ↑/↓/Home/End gezinir, Esc kapatır ve odağı
// düğmeye döndürür, Tab kapatır, dışarı tıklama kapatır (`lib/menu-keys.ts`).

import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { SECTOR_LABELS, type KpiPackage } from "@mahir/shared";
import { menuKeyAction } from "../../lib/menu-keys";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { decisionText, footerBtn, helpText, monoStyle, pillStyle } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY;

export interface PackageEditorHeaderProps {
  pkg: KpiPackage;
  /** taslağı yansıtan sayaçlar */
  total: number;
  auto: number;
  /** biçimlenmiş son değişiklik damgası */
  stamp: string;
  frameworkName: string | null;
  archived: boolean;
  canManage: boolean;
  /** taslak var — arşivle / set kur engellenir (kayıp olmasın) */
  dirty: boolean;
  busy: boolean;
  archiving: boolean;
  onAi: () => void;
  onAdd: () => void;
  onIdentity: () => void;
  onForkSet: () => void;
  onArchiveToggle: () => void;
}

const aiBtn: CSSProperties = {
  ...footerBtn(),
  borderColor: "var(--color-ai-border)",
  color: "var(--color-ai-text)",
  background: "var(--color-ai-bg)",
};

export function PackageEditorHeader(p: PackageEditorHeaderProps): JSX.Element {
  const { pkg } = p;
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const hintId = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([disabled])')?.focus();
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    btnRef.current?.focus();
  };
  const item = (label: string, onClick: () => void, disabled = false): JSX.Element => (
    <button
      key={label}
      type="button"
      role="menuitem"
      disabled={disabled}
      aria-describedby={disabled && p.dirty ? hintId : undefined}
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      style={{
        ...decisionText,
        display: "block",
        width: "100%",
        textAlign: "left",
        minHeight: 44,
        padding: "8px 14px",
        background: "transparent",
        border: 0,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );

  return (
    <header
      className="grid gap-2"
      style={{ borderBottom: "1px solid var(--color-border)", paddingBottom: 12 }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 style={{ ...decisionText, fontSize: "1.25rem", fontWeight: 700, margin: 0 }}>
          {pkg.name}
        </h2>
        <span style={{ ...monoStyle, color: "var(--color-text-2)" }}>{pkg.code}</span>
        {pkg.sectorCode ? (
          <span style={pillStyle}>
            {SECTOR_LABELS[pkg.sectorCode as keyof typeof SECTOR_LABELS] ?? pkg.sectorCode}
          </span>
        ) : null}
        {p.archived ? (
          <span
            style={{ ...pillStyle, background: "var(--color-bg)", color: "var(--color-text-2)" }}
          >
            {C.page.archived}
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        {p.canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            {!p.archived ? (
              <>
                <button type="button" style={aiBtn} disabled={p.busy} onClick={p.onAi}>
                  {C.header.ai}
                </button>
                <button type="button" style={footerBtn(true)} disabled={p.busy} onClick={p.onAdd}>
                  {C.header.add}
                </button>
              </>
            ) : null}
            <div style={{ position: "relative" }}>
              <button
                ref={btnRef}
                type="button"
                style={footerBtn()}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                aria-label={C.header.menuLabel}
                onClick={() => setOpen((v) => !v)}
              >
                ⋯
              </button>
              {open ? (
                <div
                  ref={menuRef}
                  id={menuId}
                  role="menu"
                  aria-label={C.header.menuLabel}
                  onKeyDown={(e) => {
                    const items = [
                      ...e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'),
                    ];
                    const act = menuKeyAction(
                      e.key,
                      items.indexOf(document.activeElement as HTMLElement),
                      items.map((el) => !el.hasAttribute("disabled")),
                    );
                    if (act === null) return;
                    if (act.kind === "focus") {
                      e.preventDefault();
                      items[act.index]?.focus();
                    } else if (act.restoreFocus) {
                      e.stopPropagation();
                      close();
                    } else {
                      setOpen(false); // Tab: odak doğal akışla ilerler
                    }
                  }}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    zIndex: 20,
                    minWidth: 220,
                    background: "var(--color-surface)",
                    border: "1px solid var(--color-border)",
                    borderRadius: 8,
                    boxShadow: "var(--shadow-md, 0 8px 24px rgba(15,42,82,.16))",
                  }}
                >
                  {item(C.menu.identity, p.onIdentity)}
                  {!p.archived ? item(C.menu.forkSet, p.onForkSet, p.dirty) : null}
                  {item(
                    p.archived ? C.menu.unarchive : C.menu.archive,
                    p.onArchiveToggle,
                    p.archiving || (p.dirty && !p.archived),
                  )}
                  {p.dirty ? (
                    <span
                      id={hintId}
                      style={{ ...helpText, display: "block", padding: "4px 14px 8px" }}
                    >
                      {C.page.archiveBlockedDraft}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      <p style={{ ...helpText, margin: 0 }} data-testid="package-summary">
        {C.header.summary(p.total, p.auto, pkg.usedBySetCount, p.stamp)}
        {p.frameworkName ? ` · ${C.page.frameworkInfo(p.frameworkName)}` : ""}
      </p>
      {pkg.description ? <p style={{ ...helpText, margin: 0 }}>{pkg.description}</p> : null}
    </header>
  );
}
